import {useRouter} from 'vue-router';
import {useCustomFetch} from '~/composables/customFetch.js';
import { classifyNetworkError } from '~/utils/networkError'
import { isRedirectFollowedError } from '~/utils/apiFetch'

/** Non-fatal: in-flight request cancelled (e.g. fast SPA navigation). */
export const REQUEST_CANCELLED_STATUS = 499

const raisePageError = (payload) => {
    throw createError(payload)
}

const raiseCancelled = () => {
    throw createError({
        statusCode: REQUEST_CANCELLED_STATUS,
        statusMessage: 'Request Cancelled',
        fatal: false,
    })
}

export const fetchWithErrorHandling = async (url, opts = {}, lazy = false) => {
    const router = useRouter();

    const {data, error} = await useCustomFetch(url, opts, lazy);

    if (error.value) {
        if (isRedirectFollowedError(error.value)) {
            raiseCancelled()
        }

        const classification = classifyNetworkError(error.value)
        const statusCode = classification.statusCode

        if (classification.isAborted) {
            raiseCancelled()
        }

        if (classification.isOffline) {
            return raisePageError({
                statusCode: 503,
                statusMessage: 'Temporary Network Error',
                message: error.value.message,
                fatal: false,
            });
        }

        if (statusCode === 410) {
            return raisePageError({statusCode: 410, statusMessage: 'Page Is Gone'});
        }

        if (statusCode === 405) {
            return raisePageError({statusCode: 404, statusMessage: 'Page Not Found'});
        }

        if (statusCode === 401) {
            await navigateTo(`/login?redirect=${router.currentRoute.value.fullPath}`);
            raiseCancelled()
        }

        if ([404, 403].includes(statusCode)) {
            return raisePageError({statusCode: 404, statusMessage: 'Page Not Found', message: error.value.message});
        }

        if (classification.isNetworkNoResponse) {
            return raisePageError({
                statusCode: 503,
                statusMessage: 'Temporary Network Error',
                message: error.value.message,
            });
        }

        console.error('Error info:', {
            message: error.value.message,
            stack: error.value.stack,
            name: error.value.name,
            status: error.value.response?.status,
            statusText: error.value.response?.statusText,
            headers: error.value.response?.headers,
        })

        return raisePageError({statusCode: 500, statusMessage: 'Internal Error', message: error.value.message});
    }

    if (!data?.value) {
        return raisePageError({
            statusCode: 503,
            statusMessage: 'Temporary Network Error',
        });
    }

    return data;
};
